import { MessageSquare } from "lucide-react";
import type {
  CommentResponse,
  RuleResponse,
  KnowledgeItemResponse,
  BatchResponse,
} from "../../gen/models";
import { ThreadCommentCard } from "./ThreadCommentCard";

interface ThreadGeneralNotesProps {
  comments: CommentResponse[];
  rulesList: RuleResponse[];
  knowledgeList: KnowledgeItemResponse[];
  activeBatches: BatchResponse[];
  allBatches?: BatchResponse[];
  activeHighlightedCommentId: string | null;
  editingCommentId: string | null;
  editingText: string;
  editingRuleIds: string[];
  editingKnowledgeIds: string[];
  savingEdit: boolean;
  mentionType: "rule" | "knowledge" | null;
  mentionQuery: string;
  isComposerActive?: boolean;
  renderComposerCard?: () => React.ReactNode;
  onCardClick: (comment: CommentResponse) => void;
  onStartEdit: (comment: CommentResponse) => void;
  onCancelEdit: () => void;
  onSaveEdit: (commentId: string) => void;
  onDeleteComment: (commentId: string) => void;
  onEditTextChange: (val: string) => void;
  onSelectMentionRule: (rule: RuleResponse) => void;
  onSelectMentionKnowledge: (k: KnowledgeItemResponse) => void;
  onRemoveEditingRule: (ruleId: string) => void;
  onRemoveEditingKnowledge: (knowledgeId: string) => void;
  onInspectRule?: (ruleId: string, rule?: RuleResponse) => void;
  onInspectKnowledge?: (knowledgeId: string, knowledge?: KnowledgeItemResponse) => void;
}

export function ThreadGeneralNotes({
  comments,
  rulesList,
  knowledgeList,
  activeBatches,
  allBatches = [],
  activeHighlightedCommentId,
  editingCommentId,
  editingText,
  editingRuleIds,
  editingKnowledgeIds,
  savingEdit,
  mentionType,
  mentionQuery,
  isComposerActive = false,
  renderComposerCard,
  onCardClick,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteComment,
  onEditTextChange,
  onSelectMentionRule,
  onSelectMentionKnowledge,
  onRemoveEditingRule,
  onRemoveEditingKnowledge,
  onInspectRule,
  onInspectKnowledge,
}: ThreadGeneralNotesProps) {
  const displayBatches = allBatches.length > 0 ? allBatches : activeBatches;
  const threadLevelComments = comments
    .filter((c) => !c.message_id)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  if (threadLevelComments.length === 0 && !isComposerActive) {
    return null;
  }

  return (
    <div className="pt-6 border-t border-stone-200 dark:border-stone-800/50 space-y-3">
      <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider flex items-center gap-1.5">
        <MessageSquare className="w-3.5 h-3.5" />
        <span>General Thread Notes ({threadLevelComments.length})</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {threadLevelComments.map((c) => (
          <ThreadCommentCard
            key={c.id}
            comment={c}
            rulesList={rulesList}
            knowledgeList={knowledgeList}
            batch={displayBatches.find((b) => b.id === c.batch_id)}
            isHighlighted={c.id === activeHighlightedCommentId}
            isEditing={c.id === editingCommentId}
            editingText={editingText}
            editingRuleIds={editingRuleIds}
            editingKnowledgeIds={editingKnowledgeIds}
            savingEdit={savingEdit}
            mentionType={mentionType}
            mentionQuery={mentionQuery}
            mentionTarget="editor"
            onCardClick={onCardClick}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onSaveEdit={onSaveEdit}
            onDeleteComment={onDeleteComment}
            onEditTextChange={onEditTextChange}
            onSelectMentionRule={onSelectMentionRule}
            onSelectMentionKnowledge={onSelectMentionKnowledge}
            onRemoveEditingRule={onRemoveEditingRule}
            onRemoveEditingKnowledge={onRemoveEditingKnowledge}
            onInspectRule={onInspectRule}
            onInspectKnowledge={onInspectKnowledge}
          />
        ))}
        {isComposerActive && renderComposerCard ? (
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            {renderComposerCard()}
          </div>
        ) : null}
      </div>
    </div>
  );
}
